import React, { useEffect, useState, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { X, Camera, RefreshCw, CheckCircle2, AlertCircle, Search } from 'lucide-react';
import axios from '../../utils/axiosConfig';

const ScannerModal = ({ isOpen, onClose, onScanSuccess, slug }) => {
    const [scanner, setScanner] = useState(null);
    const [cameras, setCameras] = useState([]);
    const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
    const [isScanning, setIsScanning] = useState(false);
    const [scanResult, setScanResult] = useState(null); // { success: bool, message: string, data?: any }
    const [loading, setLoading] = useState(false);
    const scannerRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            // Obtener cámaras disponibles
            Html5Qrcode.getCameras().then(devices => {
                if (devices && devices.length > 0) {
                    setCameras(devices);
                    startScanner(devices[0].id);
                }
            }).catch(err => {
                console.error("Error obteniendo cámaras", err);
            });
        } else {
            stopScanner();
        }

        return () => {
            stopScanner();
        };
    }, [isOpen]);

    const startScanner = (cameraId) => {
        const html5QrCode = new Html5Qrcode("reader");
        setScanner(html5QrCode);
        setIsScanning(true);
        setScanResult(null);

        html5QrCode.start(
            cameraId,
            {
                fps: 10,
                qrbox: { width: 250, height: 250 }
            },
            (decodedText) => {
                handleScan(decodedText, html5QrCode);
            },
            (errorMessage) => {
                // Ignorar errores de escaneo (no se encontró QR en el frame)
            }
        ).catch(err => {
            console.error("Error iniciando scanner", err);
            setIsScanning(false);
        });
    };

    const stopScanner = async () => {
        if (scanner && scanner.isScanning) {
            try {
                await scanner.stop();
                setScanner(null);
                setIsScanning(false);
            } catch (err) {
                console.error("Error deteniendo scanner", err);
            }
        }
    };

    const handleScan = async (decodedText, scannerInstance) => {
        if (loading) return;

        setLoading(true);
        // Detener escaneo temporalmente para procesar
        try {
            // pause(false) mantiene el video visible pero detiene el procesamiento de frames
            await scannerInstance.pause(false);

            const apiPath = slug ? `/${slug}/api/tickets/validate` : '/api/tickets/validate';
            const response = await axios.post(apiPath, { qrCode: decodedText });

            setScanResult({
                success: response.data.valid,
                message: response.data.message,
                data: response.data.ticket
            });

            if (response.data.valid && onScanSuccess) {
                onScanSuccess();
            }

        } catch (err) {
            setScanResult({
                success: false,
                message: err.response?.data?.message || "Error al validar el ticket"
            });
        } finally {
            setLoading(false);
        }
    };

    const resumeScanning = async () => {
        if (scanner) {
            setScanResult(null);
            await scanner.resume();
        }
    };

    const switchCamera = async () => {
        if (cameras.length < 2) return;

        await stopScanner();
        const nextIndex = (currentCameraIndex + 1) % cameras.length;
        setCurrentCameraIndex(nextIndex);
        startScanner(cameras[nextIndex].id);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div className="bg-surface w-full max-w-lg rounded-[40px] border border-white/10 shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">

                {/* Header */}
                <div className="px-8 py-6 flex items-center justify-between border-b border-white/5">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-2xl bg-primary-orange/20 flex items-center justify-center text-primary-orange">
                            <Search size={22} />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-white tracking-tight">Escáner de entradas</h2>
                            <p className="text-[10px] font-bold text-white/30 uppercase tracking-[2px]">Validación en tiempo real</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {cameras.length > 1 && (
                            <button
                                onClick={switchCamera}
                                className="p-3 rounded-2xl bg-white/5 text-white/60 hover:text-white hover:bg-white/10 transition-all active:scale-95"
                                title="Cambiar Cámara"
                            >
                                <RefreshCw size={20} />
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="p-3 rounded-2xl bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all active:scale-95"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Scanner Area */}
                <div className="relative p-8">
                    <div className="relative aspect-square w-full max-w-[320px] mx-auto rounded-[32px] overflow-hidden bg-black border-4 border-white/5 shadow-inner">
                        <div id="reader" className="w-full h-full"></div>

                        {/* Overlay de escaneo */}
                        {isScanning && !scanResult && (
                            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                                <div className="w-48 h-48 border-2 border-primary-orange/50 rounded-3xl relative">
                                    <div className="absolute inset-0 border-2 border-primary-orange rounded-3xl animate-pulse"></div>
                                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-0.5 bg-primary-orange shadow-[0_0_15px_rgba(249,115,22,0.8)] animate-scan-line"></div>
                                </div>
                            </div>
                        )}

                        {/* Resultado del escaneo */}
                        {scanResult && (
                            <div className={`absolute inset-0 flex flex-col items-center justify-center p-6 text-center animate-in fade-in zoom-in duration-300 ${scanResult.success ? 'bg-green-500/90' : 'bg-red-500/90'
                                } backdrop-blur-md`}>
                                {scanResult.success ? (
                                    <CheckCircle2 size={64} className="text-white mb-4 animate-bounce" />
                                ) : (
                                    <AlertCircle size={64} className="text-white mb-4 animate-shake" />
                                )}
                                <h3 className="text-2xl font-black text-white mb-2">{scanResult.success ? '¡Válido!' : 'Error'}</h3>
                                <p className="text-white/90 font-bold mb-6">{scanResult.message}</p>

                                {scanResult.success && scanResult.data && (
                                    <div className="bg-white/20 rounded-2xl p-4 mb-6 w-full text-left">
                                        <p className="text-[10px] font-bold text-white/60 uppercase tracking-widest mb-1">Comprador</p>
                                        <p className="text-white font-bold truncate mb-2">{scanResult.data.userName}</p>
                                        <p className="text-[10px] font-bold text-white/60 uppercase tracking-widest mb-1">Evento</p>
                                        <p className="text-white font-bold truncate">{scanResult.data.eventName}</p>
                                    </div>
                                )}

                                <button
                                    onClick={resumeScanning}
                                    className="w-full py-4 bg-white text-black rounded-2xl font-black uppercase tracking-widest shadow-xl active:scale-95 transition-all"
                                >
                                    Siguiente Ticket
                                </button>
                            </div>
                        )}

                        {loading && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center backdrop-blur-sm">
                                <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Info */}
                <div className="px-8 py-6 bg-white/5 flex items-center justify-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                    <p className="text-xs font-bold text-white/40 uppercase tracking-widest">
                        {cameras[currentCameraIndex]?.label || "Buscando cámara..."}
                    </p>
                </div>
            </div>

            <style>{`
        @keyframes scan-line {
          0% { top: 0%; }
          100% { top: 100%; }
        }
        .animate-scan-line {
          animation: scan-line 2s linear infinite;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
        .animate-shake {
          animation: shake 0.2s ease-in-out 0s 2;
        }
      `}</style>
        </div>
    );
};

export default ScannerModal;
